import { MessageSendResponseDTO } from "../generated";
import { chunkArray } from "./chunkArray";

/** API 요청 본문 크기 제한(413) 회피용 — 수신자 목록 분할 크기 */
export const RECIPIENT_BATCH_SIZE = 1000;

export class BatchSendError extends Error {
  constructor(
    message: string,
    readonly sentCount: number,
    readonly failedBatch: number,
    readonly totalBatches: number
  ) {
    super(message);
    this.name = "BatchSendError";
  }
}

type BatchSendParams = { recipientList: unknown[] };

/**
 * recipientList가 RECIPIENT_BATCH_SIZE를 넘으면 여러 번 API를 호출합니다.
 * 1000명 이하면 sendFn을 1회만 호출합니다.
 */
export async function sendRecipientListInBatches<P extends BatchSendParams>(
  params: P,
  sendFn: (payload: P) => Promise<{ data: MessageSendResponseDTO }>
): Promise<{ data: MessageSendResponseDTO }> {
  const chunks = chunkArray(params.recipientList, RECIPIENT_BATCH_SIZE);

  if (chunks.length === 0) {
    throw new Error("수신자 목록이 비어 있습니다");
  }

  let sentCount = 0;
  let lastResponse: { data: MessageSendResponseDTO } | undefined;

  for (let i = 0; i < chunks.length; i++) {
    const response = await sendFn({
      ...params,
      recipientList: chunks[i],
    } as P);

    if (!response.data.header.isSuccess) {
      throw new BatchSendError(
        response.data.header.resultMessage ||
          `전송 실패 (${i + 1}/${chunks.length}회차)`,
        sentCount,
        i + 1,
        chunks.length
      );
    }

    sentCount += chunks[i].length;
    lastResponse = response;
  }

  return lastResponse!;
}

export function getBatchSendErrorMessage(error: unknown): string | null {
  if (error instanceof BatchSendError) {
    if (error.sentCount > 0) {
      return `${error.sentCount}명까지 전송되었으나 이후 전송에 실패했습니다. 발송 내역을 확인해주세요.`;
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "전송 중 오류가 발생했습니다";
}
